<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { McTypeaheadMultiSelect, McTooltip } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import {
  FilterType,
  FilterTypeEnum,
  useApplyFilter,
  useLookupFetcher,
  FilterDefinition,
  formatOptionLabel
} from "@/features/filters";
import { useGlobalOptions } from "@/features/filters/composables/useGlobalOptions/useGlobalOptions";
import { normalisedText } from "@/global";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}>();

const emit = defineEmits<
  /** Fired when the user scrolls to the bottom of the option list. */
  (e: "listscroll") => void
>();

const {
  key: filterKey,
  label: filterLabel = "",
  placeholder = "",
  hiddenlabel,
  lookupUrl,
  config,
  dependencies,
  options: optionsProp,
  sectionLabel,
  groupId,
  groupLabel,
  searchParam,
  debounce,
  maxOptions,
  minChars,
  clearButton,
  freetexttagging,
  inputParams,
  enableFetchDependentOptions,
  disableUntilParentSelected,
  extractPayloadFromOption,
  paginatedOptions,
  globalOptionsKey,
  clearDependencies: clearDependenciesKeys
} = props.filter;

const { t } = useI18n();

const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const searchText = ref("");
const hasUserCleared = ref(false);
const zone = props.zonePrefix || "default";

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeValue = props.filter.notApplicable ? undefined : source?.[zone]?.[filterKey];

  if (!storeValue) return { value: [], label: "", filterType: props.filterType };

  if (Array.isArray(storeValue.value)) {
    return { ...storeValue, filterType: props.filterType };
  }

  return {
    value: storeValue.value ? [{ label: storeValue.label, value: storeValue.value }] : [],
    label: storeValue.label || "",
    filterType: props.filterType
  };
});

const resolvedDependencies = computed(() => {
  const deps: Record<string, any> = {};
  if (!dependencies) return deps;

  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;

  for (const [param, dependsOn] of Object.entries(dependencies)) {
    const dep = source?.[zone]?.[dependsOn];
    deps[param] = typeof dep === "object" ? dep?.value : dep;
  }

  return deps;
});

// if the parent filter is updated, refetch the options
watch(resolvedDependencies, () => {
  if (enableFetchDependentOptions) fetch();
});

/**
 * Clear dependent filters when the current filter's selection changes
 */
const clearDependencies = () => {
  clearDependenciesKeys?.forEach((dependentFilterKey) => {
    removeFilter(dependentFilterKey);
  });
};

const {
  options: fetchedOptions,
  fetch,
  loadMore,
  hasMore,
  loading,
  loadingMore
} = useLookupFetcher(
  filterKey,
  lookupUrl || "",
  config || {},
  { forceRefresh: false, autoFetchEnabled: false },
  resolvedDependencies,
  inputParams,
  searchParam,
  searchText
);

const globalOptionsHook = globalOptionsKey ? useGlobalOptions(globalOptionsKey) : null;

if (globalOptionsHook) {
  // Lazy fetch on first mount of the filter component.
  globalOptionsHook.fetch();
}

/**
 * disable the filter if dependent filter is not selected/inputed
 */
const isDisabled = computed(() => {
  if (props.filter.disabled) return true;
  return disableUntilParentSelected && dependencies
    ? !Object.values(resolvedDependencies.value).some((val) => val !== null && val !== undefined && val !== "")
    : false;
});

// Free-text-only filters have no data source; guide users instead of showing
// "No suggestions found" (which is misleading) or an empty dropdown box.
// optionsProp can be [] (empty array) when no static options are configured —
// an empty array is truthy so we must check length, not just presence.
const isFreetextOnly = computed(() => !!(freetexttagging && !lookupUrl && !optionsProp?.length && !globalOptionsKey));
const noSuggestionsText = computed(() => (isFreetextOnly.value ? undefined : "No suggestions found"));

const options = computed(() => {
  const rawOptions =
    (globalOptionsHook ? globalOptionsHook.options.value : null) || optionsProp || fetchedOptions.value;
  const formatTemplate = props.filter.optionLabelFormat;
  const normaliseText = props.filter.normaliseText;
  const subLabelKey = props.filter.subLabelKey;

  if (!formatTemplate && !normaliseText)
    return rawOptions.map((opt) => ({ ...opt, ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}) }));

  return rawOptions.map((opt) => {
    let label = opt.label;
    if (normaliseText) label = normalisedText(label) || label;
    if (formatTemplate) label = formatOptionLabel(opt as Record<string, any>, formatTemplate) || label;
    return {
      ...opt,
      ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}),
      label
    };
  });
});

// When operating in paginated API-fetch mode, maxoptions must equal the number
// of loaded items so the full list is rendered and the scroll container can
// actually overflow.  If it stays at 10, only 10 items are rendered and the
// list is never tall enough to scroll → listscroll never fires.
const effectiveMaxOptions = computed(() =>
  paginatedOptions && !optionsProp && fetchedOptions.value.length > 0 ? fetchedOptions.value.length : maxOptions || 10
);

const onInput = (e: any) => {
  const text = e?.target?.value || "";
  searchText.value = text;
  hasUserCleared.value = text.trim() === "";
};

const onSelect = (event: any) => {
  const payload = extractPayloadFromOption ? event?.detail?.map(extractPayloadFromOption) : [];

  const selectedValues = event?.detail?.map((opt: any) => ({
    ...opt,
    label: opt.label,
    value: opt.value
  }));

  // clear dependent filters only if the current selection actually changed
  if (clearDependenciesKeys?.length) {
    const previousKeys = (Array.isArray(selectedFilter.value.value) ? selectedFilter.value.value : [])
      .map((opt: any) => opt.value)
      .sort()
      .join(",");
    const nextKeys = (selectedValues || [])
      .map((opt: any) => opt.value)
      .sort()
      .join(",");
    if (previousKeys !== nextKeys) clearDependencies();
  }

  applyFilter(filterKey, {
    filterKey,
    value: selectedValues,
    filterType: props.filterType,
    ...(extractPayloadFromOption ? { payload, extractPayloadFromOption } : {}),
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  });
};

const onSearch = () => {
  if (searchText.value.trim().length === 0) return;
  fetch();
};

const onClear = () => {
  removeFilter(filterKey);
  clearDependencies();
};

// ─── Scroll-to-end detection ────────────────────────────────────────────────

// Reference to the mc-typeahead-multi-select DOM element.
const typeaheadEl = ref<any>(null);

/**
 * The MDS component tracks scroll position in `lastScrollTop` (instance
 * property, not reactive).  It only initialises it to 0 inside
 * `resetToDefaultState()`, which is called from `handleInputChange()` —
 * meaning only when the user types.  When the dropdown is opened via focus
 * alone the property stays `null`, so the internal guard
 *   `this.lastScrollTop != null`
 * always fails and `listscroll` is never dispatched.
 *
 * Setting it to 0 here unblocks the scroll chain.
 */
const onFocus = async () => {
  if (!paginatedOptions) return;
  await fetch();
  if (typeaheadEl.value && typeaheadEl.value.lastScrollTop == null) {
    typeaheadEl.value.lastScrollTop = 0;
  }
};

const onListScroll = () => {
  emit("listscroll");
  if (paginatedOptions && hasMore.value && !loading.value) {
    loadMore();
  }
};

// ─── Component prop config ───────────────────────────────────────────────────

/**
 * Props that are exclusively driven by the freetext-only mode.
 * Centralising them here makes it easy to see—and change—what the component
 * does differently when there is no data source backing the filter.
 */
const freetextProps = computed(() => ({
  trailingicon: freetexttagging ? "info-circle" : "magnifying-glass",
  nosuggestions: noSuggestionsText.value,
  optionswidth: isFreetextOnly.value ? "0px" : undefined,
  optionsheight: isFreetextOnly.value ? "0px" : undefined
}));
</script>

<template>
  <div class="typeahead-filter-wrapper">
    <mc-typeahead-multi-select
      ref="typeaheadEl"
      fit="small"
      highlight
      :infinitescroll="paginatedOptions || undefined"
      :name="filterKey"
      :label="t(filterLabel)"
      :placeholder="t(placeholder)"
      :hiddenlabel="hiddenlabel"
      :data="
        options.map((option) => ({
          ...option,
          label: t(option.label)
        }))
      "
      :selecteddata="selectedFilter?.value || []"
      :loading="!optionsProp && loading && (!paginatedOptions || fetchedOptions.length === 0)"
      :debounce="debounce || 300"
      :maxoptions="effectiveMaxOptions"
      :min-chars="minChars || 3"
      :clearbutton="clearButton"
      :freetexttagging="freetexttagging"
      :disabled="isDisabled"
      :data-test="`filter-text-${filterKey}`"
      v-bind="freetextProps"
      @focus="onFocus"
      @input="onInput"
      @optionselected="onSelect"
      @search="onSearch"
      @listscroll="onListScroll"
      @clearbuttonclick="onClear"
    >
      <template v-if="paginatedOptions">
        <div v-if="!optionsProp && fetchedOptions.length > 0" class="custom-option-container">
          <template v-if="loadingMore">
            <mc-loading-indicator fit="small" orientation="horizontal" :label="t('LOADING_MORE_OPTIONS')" />
          </template>
          <template v-else>
            {{ t("ALL_OPTIONS_LOADED") }}
          </template>
        </div>
        <div v-else-if="hasMore" class="custom-option-container">
          {{ t("TYPE_IN_INPUT_TO_SEARCH_FOR_MORE_OPTIONS") }}
        </div>
      </template>
    </mc-typeahead-multi-select>

    <mc-tooltip v-if="freetexttagging" fit="small" position="top-right" class="freetext-hint-tooltip">
      <span slot="trigger" class="freetext-hint-hotspot" />
      <span>{{ t("TYPE_AND_PRESS_ENTER_TO_ADD") }}</span>
    </mc-tooltip>
  </div>
</template>
<style lang="scss" scoped>
.typeahead-filter-wrapper {
  position: relative;
}

.freetext-hint-tooltip {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  z-index: 10;
}

.freetext-hint-hotspot {
  display: block;
  width: 36px;
  height: 36px;
  cursor: help;
}

.custom-option-container {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  font-size: 12px;
  color: var(--mds_core_color_grey_600, #6b7280);
  border-top: 1px solid var(--mds_core_color_grey_200, #e5e7eb);
}
</style>
